import { Key, ReactNode, useCallback, useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, TextField } from '@mui/material';
import axios from 'axios';

import { Notistack } from '@shared/components/Notistack/Notistack';
import { useDebounce } from '@shared/hooks/useDebounce';
import { withQueryParams } from '@shared/commonFunctions';
import { AutoCompleteWrapper, InputLabel } from '@styles/InputStyles';
import { IOption } from '@shared/interfaces';
import { getError } from '@shared/utils/helpers';

const DEFAULT_PAGE_SIZE = 50;

export interface ISearchInputProps<T, V = string> {
  fetchUrl: string;
  mapOptionsFn: (option: T) => IOption<V>;
  defaultValue?: IOption<V>;
  onChange?: (value: string, fullValueObj?: IOption<unknown> | null) => void;
  label?: string;
  backgroundColor?: string;
  multiple?: boolean;
  margin?: string;
  removeExtraPadding?: boolean;
  placeholderOpacity?: number;
  highlightFullBorder?: boolean;
  helperText?: ReactNode;
  error?: boolean;
  params?:any;
}

export const SearchInput = <T, V>({
  fetchUrl,
  label,
  onChange,
  mapOptionsFn,
  defaultValue,
  multiple = false,
  margin,
  backgroundColor,
  removeExtraPadding,
  placeholderOpacity,
  highlightFullBorder,
  params
}: ISearchInputProps<T, V>) => {
  const [value, setValue] = useState<IOption | null>(defaultValue || null);
  const [inputValue, setInputValue] = useState<string>();
  const [isLoading, setLoadingStatus] = useState(false);
  const [options, setOptions] = useState<IOption<V>[]>([]);
  const debouncedSearchValue = useDebounce(inputValue);
  
  const fetcher = useCallback(
    async (searchInput = '') => {
      setLoadingStatus(true);

      try {
        const response = await axios.get<{ items: T[], totalCount: number }>(withQueryParams(
          fetchUrl,
          Object.assign({},{
            pageNumber: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            search: searchInput
          }, params),
          false
        ));

        const opts = response.data.items.map(mapOptionsFn);
        setOptions(opts);
      } catch (err) {
        Notistack.generalError(getError(err));
      } finally {
        setLoadingStatus(false);
      }
    },
    []
  );

  useEffect(
    () => {
      setValue(defaultValue || null);
    },
    [defaultValue]
  );

  useEffect(
    () => {
      fetcher(debouncedSearchValue);
    },
    [debouncedSearchValue]
  );

  return (
    <AutoCompleteWrapper
      labeled={Boolean(label)}
      multiple={multiple}
      margin={margin}
      backgroundColor={backgroundColor}
      removeExtraPadding={removeExtraPadding}
      placeholderOpacity={placeholderOpacity}
      highlightFullBorder={highlightFullBorder}
    >
      {label && <InputLabel>{label}</InputLabel>}

      <Autocomplete
        autoComplete
        includeInputInList
        clearIcon={<ClearIcon fontSize="small" />}
        getOptionLabel={(option) => option.label}
        filterOptions={(x) => x}
        options={options}
        value={value}
        loading={isLoading}
        noOptionsText="No Options"
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        renderInput={(params) => (
          <TextField
            {...params}
            onChange={(event) => setInputValue(event.target.value)}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value as Key}>
            {option.label}
          </li>
        )}
        onChange={(_, newValue) => {
          onChange?.(newValue?.value as string, newValue);
          setValue(newValue);
        }}
      />
    </AutoCompleteWrapper>
  );
};